import { CartErrorEvent, CartLinesUpdateEvent } from '@shopify/events';
import { cropOf, place, ratioOf } from './gn-print-crop.js';

/**
 * GN — Componi la tua parete (sections/gn-wall-builder.liquid).
 * Ambiente in scala reale (arredi generati con Codex, WebP senza sfondo), composizioni in centimetri,
 * stampe scelte toccando i quadri e ritagliate esattamente sulla grafica, una cornice per tutta la parete,
 * tutto al carrello con un solo POST. Lo sconto quantità lo applica Shopify: qui si mostra soltanto.
 */
const SIZES = { a4: [21, 29.7, 'A4'], a3: [29.7, 42, 'A3'], '50x70': [50, 70, '50×70'] };
const GAP = 6; // cm tra un quadro e l'altro
// Composizioni a colonne (da sinistra), quadri dall'alto. Le posizioni si calcolano dall'orientamento delle
// stampe scelte: una stampa orizzontale (es. La danza) gira il suo quadro e la composizione si risistema.
const LAYOUTS = [
  { id: 'coppia', name: 'Coppia', cols: [['a3'], ['a3']] },
  { id: 'colonna', name: 'Colonna', cols: [['a3', 'a3']] },
  { id: 'duo', name: 'Duo grande', cols: [['50x70'], ['50x70']] },
  { id: 'trittico', name: 'Trittico', cols: [['a3'], ['a3'], ['a3']] },
  { id: 'protagonista', name: 'Protagonista', cols: [['50x70'], ['a4', 'a4']] },
  { id: 'griglia', name: 'Griglia', cols: [['a4', 'a4'], ['a4', 'a4']] },
  { id: 'fila', name: 'Fila', cols: [['a4'], ['a4'], ['a4'], ['a4']] },
  { id: 'galleria', name: 'Galleria', cols: [['a4', 'a4'], ['50x70'], ['a4', 'a4']] },
  { id: 'mosaico', name: 'Mosaico', cols: [['a3', 'a4'], ['a4', 'a3'], ['a3', 'a4']] },
  { id: 'piena', name: 'Parete piena', cols: [['a4', 'a4'], ['a3', 'a4'], ['50x70'], ['a4', 'a3'], ['a4', 'a4']] },
];
const keysOf = (layout) => layout.cols.flat();
// Arredo sotto i quadri: larghezza reale dell'immagine, spazio dal bordo basso dei quadri, quanta parte si vede
// ratio = altezza/larghezza dell'immagine dell'arredo (per farlo poggiare a terra quando c'è spazio)
// base: dove l'arredo tocca la parete (frazione dell'altezza dell'immagine): lì passa la linea del pavimento.
// Le gambe davanti scendono più in basso, sul pavimento, come in una foto vera.
// center: il punto dell'immagine che va sotto il centro dei quadri (il divano, non il divano più il tavolino)
const ROOMS = [
  { id: 'salone', name: 'Salone', width: 280, gap: 25, visible: 55, ratio: 367 / 1367, base: 0.93, center: 0.556 },
  { id: 'cucina', name: 'Cucina', width: 300, gap: 20, visible: 45, ratio: 605 / 1363, base: 0.99 },
  { id: 'camera', name: 'Camera da letto', width: 200, gap: 18, visible: 60, ratio: 509 / 1263, base: 0.72 },
  { id: 'studio', name: 'Studio', width: 120, gap: 16, visible: 62, ratio: 818 / 960, base: 0.856 },
  { id: 'pizzeria', name: 'Pizzeria', width: 260, gap: 18, visible: 60, ratio: 610 / 1357, base: 0.735 },
  { id: 'ufficio', name: 'Ufficio', width: 180, gap: 16, visible: 60, ratio: 746 / 1271, base: 0.75 },
  { id: 'bar', name: 'Bar', width: 210, gap: 18, visible: 75, ratio: 792 / 1221, base: 0.906 },
];
const FRAME_COLORS = { nero: '#1d1d1b', bianco: '#ffffff', legno: '#b88a5a' };
// I colori di parete più diffusi nelle case italiane
const WALLS = [
  ['Bianco latte', '#f2efe9'], ['Grigio perla', '#d8d7d2'], ['Tortora', '#bfb2a3'], ['Sabbia', '#e3d4bc'],
  ['Verde salvia', '#aeb79c'], ['Carta da zucchero', '#a8bdcc'], ['Terracotta', '#c67b5c'], ['Antracite', '#4b4c4e'],
];
// Colori del filtro nel drawer (valori del campo Colore dei prodotti)
const SWATCH = { Arancione: '#f08a24', Beige: '#e3d4bc', Bianco: '#ffffff', Blu: '#2f5fd0', Giallo: '#f6d23c', Marrone: '#7a5236', Multicolore: 'conic-gradient(#e84e24,#ffd96f,#3aa655,#2f6fd6,#e45aa0,#e84e24)', Nero: '#1d1d1b', Rosa: '#f4a6c1', Rosso: '#d8262c', Verde: '#3aa655' };
const ICONS = {
  salone: '<path d="M4 11V8a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v3"/><path d="M2 13a2 2 0 0 1 4 0v2h12v-2a2 2 0 0 1 4 0v4H2z"/><path d="M5 17v2M19 17v2"/>',
  cucina: '<path d="M3 10h18"/><path d="M5 10v9M19 10v9"/><path d="M9 6h2v4H9zM14 7c0-1.5 2-1.5 2-3"/>',
  camera: '<path d="M3 18V8M21 18v-5H3"/><path d="M3 13h18"/><rect x="5" y="9" width="5" height="4" rx="1"/>',
  studio: '<path d="M3 10h18M5 10v9M19 10v9"/><path d="M8 10 6 5l3-1M6 5h3"/><rect x="13" y="13" width="4" height="6"/>',
  pizzeria: '<path d="M12 3 3 19a17 17 0 0 0 18 0z"/><circle cx="10" cy="13" r="1.2"/><circle cx="14" cy="15" r="1.2"/><circle cx="12" cy="9" r="1"/>',
  ufficio: '<rect x="3" y="4" width="18" height="11" rx="1"/><path d="M9 20h6M12 15v5"/>',
  bar: '<path d="M4 9h12v5a5 5 0 0 1-5 5H9a5 5 0 0 1-5-5z"/><path d="M16 11h2a2 2 0 0 1 0 4h-2"/><path d="M8 3v3M12 3v3"/>',
};
const euro = (cents) => `€${(cents / 100).toFixed(2).replace('.', ',')}`;

class GnWallBuilder extends HTMLElement {
  connectedCallback() {
    this.data = JSON.parse(document.querySelector('[data-gn-wall-data]').textContent);
    this.prints = this.data.prints;
    this.rooms = JSON.parse(this.dataset.rooms || '{}');
    this.landscape = new Set(); // indici delle stampe orizzontali, rilevate dalla foto
    const wanted = new URLSearchParams(location.search).get('stampa');
    this.featured = Math.max(-1, this.prints.findIndex((p) => p.h === wanted));
    this.layout = LAYOUTS.find((l) => l.id === 'protagonista');
    this.room = ROOMS[0];
    this.frame = 'nero'; // di default la parete si vede già incorniciata
    this.wallColor = WALLS[0];
    this.picks = [];
    this.filter = { tema: null, colore: null };
    this.$ = (sel) => this.querySelector(sel);

    this.renderChips();
    this.fill();
    this.render();

    this.$('[data-rooms]').addEventListener('change', (e) => this.setRoom(ROOMS.findIndex((r) => r.id === e.target.value)));
    this.$('[data-frames]').addEventListener('change', (e) => { this.frame = e.target.value; this.render(); });
    // Colori della parete: pallini dentro l'anteprima
    this.$('[data-fs-walls]').addEventListener('click', (e) => {
      const b = e.target.closest('[data-wall-index]');
      if (b) this.setWall(Number(b.dataset.wallIndex));
    });
    this.$('[data-layouts]').addEventListener('change', (e) => {
      this.layout = LAYOUTS.find((l) => l.id === e.target.value);
      this.querySelectorAll('[data-layout-pop] [data-go-layout]').forEach((b) => b.setAttribute('aria-pressed', String(b.dataset.goLayout === this.layout.id)));
      this.fill();
      this.render();
    });
    // Schermo intero: scorrere in orizzontale cambia stanza, in verticale il colore della parete
    let start = null;
    this.$('[data-wall]').addEventListener('touchstart', (e) => {
      start = this.classList.contains('is-full') && e.touches.length === 1 ? { x: e.touches[0].clientX, y: e.touches[0].clientY } : null;
      this.$('[data-coach]').hidden = true;
    }, { passive: true });
    this.$('[data-wall]').addEventListener('touchend', (e) => {
      if (!start) return;
      const dx = e.changedTouches[0].clientX - start.x;
      const dy = e.changedTouches[0].clientY - start.y;
      start = null;
      if (Math.max(Math.abs(dx), Math.abs(dy)) < 40) return; // è un tocco, non uno scorrimento
      this.classList.add('is-swiped');
      if (Math.abs(dx) > Math.abs(dy)) this.setRoom(ROOMS.indexOf(this.room) + (dx < 0 ? 1 : -1));
      else this.setWall(WALLS.indexOf(this.wallColor) + (dy < 0 ? 1 : -1));
    }, { passive: true });
    this.$('[data-shuffle]').addEventListener('click', (e) => {
      this.fill(true);
      this.render();
      const b = e.currentTarget;
      b.classList.remove('is-spinning');
      void b.offsetWidth; // riavvia l'animazione
      b.classList.add('is-spinning');
    });
    this.$('[data-full]').addEventListener('click', () => this.toggleFull());
    document.addEventListener('keydown', (e) => { if (e.key === 'Escape' && this.classList.contains('is-full')) this.toggleFull(false); });
    this.querySelectorAll('[data-add]').forEach((b) => b.addEventListener('click', () => this.addToCart(b)));
    this.addEventListener('click', (e) => {
      const slot = e.target.closest('[data-slot]');
      if (slot) return this.openPicker(Number(slot.dataset.slot));
      const go = e.target.closest('[data-go-layout]');
      if (!go) return;
      const pop = this.$('[data-layout-pop]');
      if (pop.matches(':popover-open')) pop.hidePopover();
      const input = this.querySelector(`[data-layouts] input[value="${go.dataset.goLayout}"]`);
      if (input) { input.checked = true; input.dispatchEvent(new Event('change', { bubbles: true })); }
    });

    const picker = this.$('[data-picker]');
    this.$('[data-picker-close]').addEventListener('click', () => picker.close());
    picker.addEventListener('click', (e) => {
      if (e.target === picker) return picker.close();
      const option = e.target.closest('[data-print]');
      if (!option) return;
      this.picks[this.editing] = Number(option.dataset.print);
      this.classList.add('is-taught'); // primo cambio fatto: il suggerimento si ritira
      picker.close();
      this.render();
      this.querySelector(`.gn-wall__slot[data-slot="${this.editing}"]`)?.focus();
    });
    this.$('[data-search]').addEventListener('input', () => this.renderOptions());
    // Filtri del drawer: un tema e un colore alla volta, un secondo tocco li toglie
    picker.addEventListener('click', (e) => {
      const chip = e.target.closest('[data-filter]');
      if (!chip) return;
      const [kind, value] = chip.dataset.filter.split(':');
      this.filter[kind] = this.filter[kind] === value ? null : value;
      this.renderFilters();
      this.renderOptions();
    });
    new ResizeObserver(() => this.renderWall()).observe(this.$('[data-wall]'));
  }

  renderChips() {
    const id = this.dataset.sectionId;
    this.$('[data-rooms]').innerHTML = ROOMS.map((r, i) =>
      `<label class="gn-wall__chip"><input type="radio" name="gn-wall-room-${id}" value="${r.id}" ${i === 0 ? 'checked' : ''}><span><svg viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round">${ICONS[r.id]}</svg>${r.name}</span></label>`).join('');
    this.$('[data-layouts]').innerHTML = LAYOUTS.map((l) => {
      const g = this.geometry(l, []);
      const k = 18 / Math.max(g.bw, g.bh * 1.3);
      const rects = g.slots.map((t) => `<rect x="${(t.x * k).toFixed(1)}" y="${(t.y * k).toFixed(1)}" width="${(t.w * k).toFixed(1)}" height="${(t.h * k).toFixed(1)}" fill="currentColor"/>`).join('');
      return `<label class="gn-wall__chip"><input type="radio" name="gn-wall-layout-${id}" value="${l.id}" ${l === this.layout ? 'checked' : ''}><span><svg viewBox="-2 -2 ${(g.bw * k + 4).toFixed(1)} ${(g.bh * k + 4).toFixed(1)}" aria-hidden="true">${rects}</svg>${l.name} · ${keysOf(l).length}</span></label>`;
    }).join('');
    // Stesse composizioni nel menu a schermo intero: un tocco passa per [data-go-layout], come "prova Galleria"
    this.$('[data-layout-pop]').innerHTML = [...this.$('[data-layouts]').children].map((label, i) =>
      `<button type="button" data-go-layout="${LAYOUTS[i].id}" aria-pressed="${LAYOUTS[i] === this.layout}">${label.querySelector('span').innerHTML}</button>`).join('');
    this.$('[data-fs-walls]').innerHTML = WALLS.map(([label, color], i) =>
      `<button type="button" data-wall-index="${i}" aria-pressed="${i === 0}" aria-label="Parete ${label.toLowerCase()}" title="${label}" style="--c:${color}"></button>`).join('');
  }

  setRoom(index) {
    this.room = ROOMS[(index + ROOMS.length) % ROOMS.length];
    this.$(`[data-rooms] input[value="${this.room.id}"]`).checked = true;
    this.renderWall();
  }

  setWall(index) {
    index = (index + WALLS.length) % WALLS.length;
    this.wallColor = WALLS[index];
    this.querySelectorAll('[data-wall-index]').forEach((b) => b.setAttribute('aria-pressed', String(Number(b.dataset.wallIndex) === index)));
    this.renderWall();
  }

  // Posizioni in cm: colonne affiancate e centrate in verticale, quadri impilati e centrati nella colonna.
  // Un quadro con una stampa orizzontale scambia larghezza e altezza.
  geometry(layout = this.layout, picks = this.picks) {
    let x = 0, i = 0;
    const cols = layout.cols.map((col) => {
      const items = col.map((s) => {
        const land = this.landscape.has(picks[i]);
        const [w, h] = SIZES[s];
        return { i: i++, s, land, w: land ? h : w, h: land ? w : h, label: land && s === '50x70' ? '70×50' : SIZES[s][2] };
      });
      return { items, w: Math.max(...items.map((t) => t.w)), h: items.reduce((a, t) => a + t.h, 0) + GAP * (items.length - 1) };
    });
    const bh = Math.max(...cols.map((c) => c.h));
    const slots = [];
    cols.forEach((c) => {
      let y = (bh - c.h) / 2;
      c.items.forEach((t) => { slots[t.i] = { ...t, x: x + (c.w - t.w) / 2, y }; y += t.h + GAP; });
      x += c.w + GAP;
    });
    return { slots, bw: x - GAP, bh };
  }

  // Riempie i quadri: tiene le scelte già fatte che hanno il formato giusto, poi le più vendute non ancora usate.
  // La stampa da cui si arriva va nel quadro più grande.
  fill(shuffle = false) {
    const keys = keysOf(this.layout);
    const area = (s) => SIZES[s][0] * SIZES[s][1];
    const main = keys.reduce((best, s, i) => (area(s) > area(keys[best]) ? i : best), 0);
    const pool = this.prints.map((_, i) => i);
    if (shuffle) pool.sort(() => Math.random() - 0.5);
    const previous = shuffle ? [] : this.picks;
    const picks = [];
    const used = new Set();
    const ok = (i, s) => i >= 0 && this.prints[i]?.v[s] && !used.has(i);
    if (this.featured >= 0 && !shuffle && ok(this.featured, keys[main])) { picks[main] = this.featured; used.add(this.featured); }
    keys.forEach((s, i) => {
      if (picks[i] !== undefined) return;
      const keep = previous.find((p) => ok(p, s));
      const pick = keep ?? pool.find((p) => ok(p, s));
      if (pick !== undefined) { picks[i] = pick; used.add(pick); }
    });
    this.picks = picks;
  }

  render() {
    this.renderWall();
    this.renderList();
    this.renderSummary();
  }

  renderWall() {
    const wall = this.$('[data-wall]');
    const W = wall.clientWidth, H = wall.clientHeight;
    if (!W) return;
    const room = this.room;
    const { slots: geo, bw, bh } = this.geometry();
    // Tutto in scala reale: quadri, spazio fino all'arredo e l'arredo, che poggia sul pavimento.
    // In alto resta una fascia per il suggerimento "Tocca un poster", così non copre mai i quadri.
    // Nell'anteprima piccola su mobile è un primo piano: arredo tagliato in basso, poster più grandi
    const small = W < 600;
    const crop = small && !this.classList.contains('is-full');
    const furnitureCm = room.width * room.ratio;
    const visible = crop ? room.visible * 0.45 : furnitureCm;
    let groundY = crop ? H : H * 0.97; // davanti all'arredo resta una striscia di pavimento
    let top = small ? 38 : Math.max(48, H * 0.07);
    // Schermo intero: l'arredo poggia sopra i comandi in basso, il pavimento continua sotto di loro
    if (this.classList.contains('is-full')) {
      const wallTop = wall.getBoundingClientRect().top;
      groundY = Math.min(groundY, ...['.gn-wall__tools', '.gn-wall__fs-add'].map((sel) => this.$(sel).getBoundingClientRect().top - wallTop - 8));
    }
    // Schermo intero su mobile: i quadri partono sotto la scritta della stanza e la pillola del risparmio
    if (small && this.classList.contains('is-full')) {
      const wallTop = wall.getBoundingClientRect().top;
      top = Math.max(top, ...['.gn-wall__status', '[data-badge]'].map((sel) => this.$(sel).getBoundingClientRect().bottom - wallTop + 12));
    }
    const scale = Math.min((W * (small ? 0.86 : 0.8)) / bw, (groundY - top - (crop ? H * 0.02 : 0)) / (bh + room.gap + visible));
    const ox = (W - bw * scale) / 2;
    // Se avanza spazio la scena scende finché l'arredo poggia sul pavimento: niente mobili sospesi
    const bottom = top + (bh + room.gap + furnitureCm) * scale;
    const oy = top + Math.max(0, groundY - bottom);
    const framed = this.frame !== 'none';
    const frame = framed ? Math.min(6, Math.max(2.5, 1.2 * scale)) : 0;
    const [wallName, wallColor] = this.wallColor;
    const dark = parseInt(wallColor.slice(1, 3), 16) * 0.3 + parseInt(wallColor.slice(3, 5), 16) * 0.59 + parseInt(wallColor.slice(5, 7), 16) * 0.11 < 140;
    wall.style.setProperty('--wall', wallColor);
    this.$('[data-status]').innerHTML = `<b>${room.name}</b> · ${wallName}`;
    wall.parentElement.style.setProperty('--wall-h', `${H}px`); // i pulsanti stanno in basso sulla parete
    wall.classList.toggle('is-dark', dark);
    wall.classList.toggle('is-framed', framed);
    // Il formato sotto il quadro solo se c'è spazio: con un altro quadro subito sotto finirebbe coperto
    const roomForTag = (t) => !geo.some((o) => o.y > t.y && o.x < t.x + t.w && o.x + o.w > t.x && (o.y - t.y - t.h) * scale < 2 * frame + 16);
    const slots = geo.map((t) => {
      const p = this.prints[this.picks[t.i]];
      const left = ox + t.x * scale, top = oy + t.y * scale;
      return `<button type="button" class="gn-wall__slot" data-slot="${t.i}" aria-label="Quadro ${t.i + 1}, ${t.label}: ${p ? p.t : 'vuoto'}. Cambia stampa"
        style="left:${left}px;top:${top}px;width:${t.w * scale}px;height:${t.h * scale}px;--frame:${frame}px;--frame-color:${framed ? FRAME_COLORS[this.frame] : 'transparent'}">
        ${p ? `<img src="${p.img}" alt="" data-print="${this.picks[t.i]}">` : ''}</button>
        ${roomForTag(t) ? `<span class="gn-wall__tag" style="left:${left + (t.w * scale) / 2}px;top:${top + t.h * scale + frame + 3}px">${t.label}</span>` : ''}`;
    }).join('');
    // Quadri in un contenitore a parte (display: contents), così l'arredo non viene ricreato a ogni
    // "Casuale": l'immagine si sostituisce solo cambiando stanza, altrimenti si sposta e basta
    const box = wall.querySelector('.gn-wall__slots') || wall.appendChild(Object.assign(document.createElement('div'), { className: 'gn-wall__slots' }));
    if (box.dataset.html !== slots) {
      box.innerHTML = slots;
      box.dataset.html = slots;
      this.cropSlots();
    }
    let sofa = wall.querySelector('.gn-wall__furniture');
    if (sofa?.dataset.room !== room.id) {
      sofa?.remove();
      sofa = this.rooms[room.id] ? wall.appendChild(Object.assign(document.createElement('img'), { className: 'gn-wall__furniture', src: this.rooms[room.id], alt: '' })) : null;
      if (sofa) sofa.dataset.room = room.id;
    }
    const furnitureW = room.width * scale;
    if (sofa) Object.assign(sofa.style, { left: `${W / 2 - furnitureW * (room.center ?? 0.5)}px`, top: `${oy + (bh + room.gap) * scale}px`, width: `${furnitureW}px` });
    // Pavimento dietro l'arredo, dalla linea in cui l'arredo tocca la parete. Nel primo piano su mobile
    // l'arredo è tagliato e quella linea cade fuori campo.
    const floor = wall.querySelector('.gn-wall__floor') || wall.insertBefore(Object.assign(document.createElement('div'), { className: 'gn-wall__floor' }), wall.firstChild);
    const floorY = oy + (bh + room.gap + furnitureCm * room.base) * scale;
    floor.style.top = `${floorY}px`;
    // Ombra morbida a terra sotto l'arredo, tra la parete e i piedini davanti
    floor.style.setProperty('--fw', `${furnitureW * 0.56}px`);
    floor.style.setProperty('--fh', `${Math.max(10, furnitureCm * (1 - room.base) * scale * 0.9)}px`);
    floor.style.setProperty('--fy', `${furnitureCm * (1 - room.base) * scale * 0.6}px`);
    wall.setAttribute('aria-label', `Anteprima: ${room.name.toLowerCase()}, parete ${wallName.toLowerCase()}`);
    this.$('[data-size]').textContent = `ingombro ${Math.round(bw)} × ${Math.round(bh)} cm`;
    // Formati della composizione: "1 × 50×70 + 2 × A4"
    const count = {};
    geo.forEach((t) => (count[t.label] = (count[t.label] || 0) + 1));
    this.$('[data-formats]').textContent = Object.entries(count).map(([l, n]) => `${n} × ${l}`).join(' + ') + ` · ingombro ${Math.round(bw)} × ${Math.round(bh)} cm`;
  }

  // Ritaglio esatto sulla grafica. Se una stampa risulta orizzontale, il suo quadro si gira e tutto si ricalcola.
  cropSlots() {
    this.$('[data-wall]').querySelectorAll('.gn-wall__slot img').forEach(async (img) => {
      const crop = await cropOf(img.src);
      if (!img.isConnected) return;
      if (!img.complete) await new Promise((r) => img.addEventListener('load', r, { once: true }));
      const index = Number(img.dataset.print);
      const land = ratioOf(crop, img.naturalWidth / img.naturalHeight) > 1;
      if (land !== this.landscape.has(index)) {
        land ? this.landscape.add(index) : this.landscape.delete(index);
        return this.render();
      }
      const box = img.parentElement;
      place(img, crop, box.clientWidth, box.clientHeight);
      img.classList.add('is-placed');
    });
  }

  toggleFull(force) {
    const on = force ?? !this.classList.contains('is-full');
    this.classList.toggle('is-full', on);
    document.documentElement.classList.toggle('gn-wall-full', on);
    const b = this.$('[data-full]');
    b.setAttribute('aria-pressed', String(on));
    b.setAttribute('aria-label', on ? 'Esci dallo schermo intero' : 'Guarda a schermo intero');
    const pop = this.$('[data-layout-pop]');
    if (!on && pop.matches(':popover-open')) pop.hidePopover();
    // Suggerimento dei gesti: solo la prima volta (ricordata nel browser) e solo su mobile
    const coach = this.$('[data-coach]');
    clearTimeout(this.coachTimer);
    coach.hidden = true;
    if (!on || !matchMedia('(max-width: 749px)').matches) return;
    try {
      if (localStorage.getItem('gn-wall-coach')) return;
      localStorage.setItem('gn-wall-coach', '1');
    } catch {}
    coach.hidden = false;
    this.coachTimer = setTimeout(() => (coach.hidden = true), 5000);
  }

  renderList() {
    const geo = this.geometry().slots;
    this.$('[data-list]').innerHTML = geo.map((t) => {
      const p = this.prints[this.picks[t.i]];
      return `<li><button type="button" class="gn-wall__row" data-slot="${t.i}">
        ${p ? `<img src="${p.img.replace('width=500', 'width=120')}" alt="" loading="lazy">` : '<span></span>'}
        <span class="gn-wall__row-name">${p ? p.t : 'Scegli una stampa'}<small>${t.label} · ${p ? euro(p.v[t.s][1]) : ''}</small></span>
        <span class="gn-wall__row-cta">Cambia</span></button></li>`;
    }).join('');
  }

  lines() {
    const lines = [];
    keysOf(this.layout).forEach((s, i) => {
      const p = this.prints[this.picks[i]];
      if (!p) return;
      lines.push({ id: p.v[s][0], price: p.v[s][1], print: true });
      const f = this.frame !== 'none' && this.data.frames[s]?.[this.frame];
      if (f) lines.push({ id: f[0], price: f[1], properties: { Montaggio: `Incluso — montata su ${p.t}` } });
    });
    return lines;
  }

  renderSummary() {
    const lines = this.lines();
    const prints = lines.filter((l) => l.print);
    const printsTotal = prints.reduce((t, l) => t + l.price, 0);
    const framesTotal = lines.reduce((t, l) => t + (l.print ? 0 : l.price), 0);
    const { q, p } = this.data.tiers;
    let pct = 0;
    q.forEach((min, i) => { if (prints.length >= min) pct = p[i]; });
    const saving = Math.round((printsTotal * pct) / 100);
    const badge = this.$('[data-badge]');
    if (badge) {
      const text = pct ? `${pct}%` : '';
      if (this.$('[data-badge-pct]').textContent !== text) {
        this.$('[data-badge-pct]').textContent = text;
        badge.classList.remove('is-pop');
        void badge.offsetWidth; // riavvia l'animazione
        badge.classList.add('is-pop');
      }
      badge.hidden = !pct;
    }
    this.$('[data-total]').textContent = euro(printsTotal - saving + framesTotal);
    this.$('[data-fs-total]').textContent = euro(printsTotal - saving + framesTotal);
    this.$('[data-total-full]').textContent = pct ? euro(printsTotal + framesTotal) : '';
    this.$('[data-saving]').textContent = pct ? `Risparmi ${euro(saving)}` : '';
    this.$('[data-saving-why]').textContent = pct ? `-${pct}% sulle stampe, perché ne prendi ${prints.length} insieme` : '';
    this.$('[data-tiers]').innerHTML = q.map((min, i) => `<li class="${prints.length >= min ? 'is-on' : ''}">${min}${i === q.length - 1 ? '+' : ''} stampe <b>-${p[i]}%</b></li>`).join('');
    const next = q.findIndex((min) => prints.length < min);
    const layoutNext = next >= 0 && LAYOUTS.find((l) => keysOf(l).length >= q[next]);
    this.$('[data-next]').innerHTML = layoutNext
      ? `Con ${q[next]} stampe risparmi il ${p[next]}%: <button type="button" data-go-layout="${layoutNext.id}">prova ${layoutNext.name}</button>`
      : 'Hai lo sconto massimo.';
    this.$('[data-frames-note]').textContent = framesTotal ? `Cornici montate incluse (${euro(framesTotal)}), lo sconto vale sulle stampe.` : '';
  }

  openPicker(slot) {
    this.editing = slot;
    const s = keysOf(this.layout)[slot];
    this.$('[data-picker-title]').textContent = `Scegli la stampa · ${SIZES[s][2]}`;
    this.$('[data-search]').value = '';
    this.filter = { tema: null, colore: null };
    this.renderFilters();
    this.renderOptions();
    this.$('[data-picker]').showModal();
    this.$('[data-options]').scrollTop = 0;
  }

  // Filtri disponibili per il formato del quadro: temi (tag tema_*) e colori (campo Colore)
  renderFilters() {
    const s = keysOf(this.layout)[this.editing];
    const pool = this.prints.filter((p) => p.v[s]);
    const count = (key) => pool.reduce((m, p) => (p[key] || []).reduce((mm, v) => mm.set(v, (mm.get(v) || 0) + 1), m), new Map());
    const themes = [...count('g')].sort((a, b) => b[1] - a[1]).map(([v]) => v);
    const colors = [...count('c')].sort((a, b) => b[1] - a[1]).map(([v]) => v).filter((v) => SWATCH[v]);
    const cap = (v) => v.charAt(0).toUpperCase() + v.slice(1).replace(/_/g, ' ');
    this.$('[data-themes]').innerHTML = themes.map((v) => `<button type="button" class="gn-pd__chip" data-filter="tema:${v}" aria-pressed="${this.filter.tema === v}">${cap(v)}</button>`).join('');
    this.$('[data-colors]').innerHTML = colors.map((v) => `<button type="button" class="gn-pd__chip" data-filter="colore:${v}" aria-pressed="${this.filter.colore === v}"><i style="--c:${SWATCH[v]}"></i>${v}</button>`).join('');
  }

  renderOptions() {
    const s = keysOf(this.layout)[this.editing];
    const q = this.$('[data-search]').value.trim().toLowerCase();
    const { tema, colore } = this.filter;
    const list = this.prints
      .map((p, i) => [p, i])
      .filter(([p]) => p.v[s] && (!q || p.t.toLowerCase().includes(q)) && (!tema || p.g?.includes(tema)) && (!colore || p.c?.includes(colore)));
    this.$('[data-count]').textContent = `${list.length} ${list.length === 1 ? 'stampa' : 'stampe'} in ${SIZES[s][2]}`;
    this.$('[data-options]').innerHTML = list
      .map(([p, i]) => `<li><button type="button" class="gn-pd__option" data-print="${i}" aria-pressed="${this.picks[this.editing] === i}">
        <img src="${p.img.replace('width=500', 'width=300')}" alt="" loading="lazy"><span>${p.t}</span><small>${euro(p.v[s][1])}</small></button></li>`)
      .join('') || '<li class="gn-pd__empty">Nessuna stampa con questi filtri.</li>';
  }

  async addToCart(button) {
    const error = this.$('[data-error]');
    const label = button.innerHTML;
    button.disabled = true;
    button.textContent = 'Aggiungo…';
    error.hidden = true;
    const sections = [...new Set([...document.querySelectorAll('cart-items-component[data-section-id]')].map((c) => c.dataset.sectionId))];
    // Tracciamento: ogni riga porta una proprietà nascosta (col _ non si vede in carrello e checkout, nell'ordine sì)
    // e il carrello un attributo, che Shopify Flow può trasformare in tag dell'ordine
    const source = `${this.layout.name} · ${this.room.name}`;
    const items = this.lines().map(({ id, properties }) => ({ id, quantity: 1, properties: { ...properties, '_Componi la tua parete': source } }));
    const promise = CartLinesUpdateEvent.createPromise();
    document.dispatchEvent(new CartLinesUpdateEvent({ action: 'add', context: 'cart', lines: items.map((i) => ({ merchandiseId: String(i.id), quantity: 1 })), promise: promise.promise, detail: { source: 'gn-wall-builder' } }));
    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items, sections: sections.join(','), sections_url: location.pathname }),
      });
      const result = await response.json();
      if (!response.ok || result.status) throw new Error(result.description || result.message || 'Non è stato possibile aggiungere la parete');
      try {
        await fetch(`${Theme.routes.cart_update_url}.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ attributes: { 'Componi la tua parete': 'Sì' } }),
        });
      } catch {} // il tracciamento non deve mai bloccare l'acquisto
      let cart = null;
      try { cart = await (await fetch(`${Theme.routes.cart_url}.js`)).json(); } catch {}
      promise.resolve({
        cart: cart ? CartLinesUpdateEvent.createCartFromAjaxResponse({ ...cart, sections: result.sections }) : null,
        detail: { sections: result.sections, items: result.items, source: 'gn-wall-builder', didError: false, refreshFailed: !cart },
      });
      button.textContent = 'Aggiunta ✓';
      this.toggleFull(false);
      // Una parete intera merita di essere vista subito: si apre il carrello (o la pagina carrello)
      const drawer = document.querySelector('#cart-drawer');
      if (drawer?.open) setTimeout(() => drawer.open(), 60);
      else location.href = Theme.routes.cart_url;
    } catch (e) {
      promise.reject(e);
      document.dispatchEvent(new CartErrorEvent({ error: e.message, code: 'INVALID', detail: { source: 'gn-wall-builder' } }));
      error.textContent = e.message;
      error.hidden = false;
      button.innerHTML = label;
    } finally {
      setTimeout(() => { button.disabled = false; button.innerHTML = label; }, 1800);
    }
  }
}

if (!customElements.get('gn-wall-builder')) customElements.define('gn-wall-builder', GnWallBuilder);
